import { FC } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Form, Input } from 'antd';

import { AppDispatch } from 'app/store';

import { setPasswordLength } from 'features/auth/authSlice';

import { SPECIAL_CHAR_REGEX, UPPERCASE_REGEX } from 'constants/regex';
import { MINIMAL_PASSWORD_LENGTH } from 'constants/uiSettings';

import { ReactComponent as EmailIcon } from 'assets/icons/inputEmail.svg';

import { useTranslation } from 'react-i18next';
import styles from './SetPasswordForm.module.scss';

interface SetPasswordFormValues {
  email: string;
  password: string;
  passwordConfirmation: string;
}

interface SetPasswordFormProps {
  email: string;
  onSubmit: (values: SetPasswordFormValues) => void;
  isLoading: boolean;
  onCancel?: () => void;
}

const SetPasswordForm: FC<SetPasswordFormProps> = ({
  email,
  onSubmit,
  isLoading,
  onCancel
}: SetPasswordFormProps) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch();

  const [form] = Form.useForm<SetPasswordFormValues>();

  return (
    <div className="login-container">
      <h2 className="title">{t('page.auth.setPassword.title')}</h2>
      <Form
        form={form}
        name={t('form.createPassword')}
        initialValues={{ email }}
        onFinish={onSubmit}
        autoComplete="off"
        className={styles.setPasswordForm}
      >
        <Form.Item name="email" label={t('form.email')} className={styles.item}>
          <Input
            type="email"
            placeholder={t('form.email.placeholder')}
            suffix={<EmailIcon />}
            disabled
            className={styles.input}
          />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('form.password.newPassword', {amount: MINIMAL_PASSWORD_LENGTH})}
          rules={[
            {
              // Custom validator to show only one error at the time
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(t('form.password.validation.create'));
                }
                if (value.length < MINIMAL_PASSWORD_LENGTH) {
                  return Promise.reject(
                    t('form.password.validation.newPasswordLength', {amount: MINIMAL_PASSWORD_LENGTH - value.length})
                  );
                }
                if (!UPPERCASE_REGEX.test(value)) {
                  return Promise.reject(
                    t('form.password.validation.uppercase')
                  );
                }
                if (!SPECIAL_CHAR_REGEX.test(value)) {
                  return Promise.reject(
                    t('form.password.validation.special')
                  );
                }
                return Promise.resolve();
              }
            }
          ]}
          className={styles.item}
        >
          <Input.Password
            placeholder={t('form.password.pleaseEnter')}
            onChange={(e) => dispatch(setPasswordLength(e.target.value.length))}
          />
        </Form.Item>
        <Form.Item
          name="passwordConfirmation"
          label={t('form.passwordConfirm')}
          dependencies={['password']}
          rules={[
            { required: true, message: t('form.passwordConfirm.validation.confirm') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('form.passwordCopy.validation.noMatch')));
              }
            })
          ]}
          className={styles.item}
        >
          <Input.Password placeholder={t('form.passwordCopy.placeholder')}/>
        </Form.Item>
        <div className={styles.buttonsContainer}>
          {onCancel && <Button onClick={onCancel}>{t('page.auth.setPassword.backToLogin')}</Button>}
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {t('page.auth.setPassword.setMyPassword')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SetPasswordForm;
